// firebase.ts
import { initializeApp } from 'firebase/app';
import { getMessaging, getToken, onMessage, type MessagePayload } from 'firebase/messaging';

import notificationsApi from '@/api/notifications/notifications.api';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const messaging = getMessaging(app);

if ('serviceWorker' in navigator) {
  navigator.serviceWorker.addEventListener('message', (event) => {
    if (event.data?.type === 'NOTIFICATION_CLICKED') {
      // Optional: handle redirect here or emit to your store
    }
  });
}

// Handle messages while app is in the foreground
onMessage(messaging, (payload: MessagePayload) => {
  if (document.hasFocus() && Notification.permission === 'granted') {
    const notificationTitle = payload.notification?.title || 'New Notification';
    const notificationOptions: NotificationOptions = {
      body: payload.notification?.body || 'You have a new message',
      icon: '/icons/icon-192x192.png',
      data: payload.data, // keep all custom data
    };

    new Notification(notificationTitle, notificationOptions);
  }
});

// Requests permission, gets the FCM token, and registers it with the backend
export const registerFcmToken = async () => {
  try {
    if (!('serviceWorker' in navigator)) {
      return;
    }

    if (Notification.permission === 'denied') {
      return;
    }

    if (Notification.permission === 'default') {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        return;
      }
    }

    const firebaseConfigParams: any = {
      apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
      authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
      projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
      storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
      messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
      appId: import.meta.env.VITE_FIREBASE_APP_ID,
    };

    const swUrl = `/firebase-messaging-sw.js?${new URLSearchParams(firebaseConfigParams).toString()}`;

    const registration = await navigator.serviceWorker.register(swUrl);

    // Wait for the service worker to be active
    await navigator.serviceWorker.ready;

    const currentToken = await getToken(messaging, {
      vapidKey: import.meta.env.VITE_FIREBASE_VAPID_KEY,
      serviceWorkerRegistration: registration,
    });

    if (currentToken) {
      await notificationsApi.create({ token: currentToken }, 'fcm-token/register');
    }
  } catch {
    //code
  }
};
